import React, { useEffect, useMemo, useState } from "react";
import {
    Backdrop,
    CircularProgress,
    Tooltip,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stack,
    TextField,
    Switch,
    FormControl,
    FormLabel,
    IconButton,
    // Alert,
} from "@mui/material";
import MDBox from "components/MDBox";
import DataTable from "comps/Tables/DataTable";
import { useOutletContext, useParams } from "react-router-dom";
import TLinkApi from "services/tlink.api";
import MDButton from "components/MDButton";
import DeleteIcon from '@mui/icons-material/Delete';
import MRTClientDataTable from "comps/Tables/DataTable/MRTClientDataTable";
import { MRT_ShowHideColumnsButton, MRT_ToggleFiltersButton, MRT_ToggleFullScreenButton } from "material-react-table";

const LIST_ENDPOINT = "/configurations/router-survey-priority";                 // GET
const CREATE_ENDPOINT = "/configurations/router-survey-priority";               // POST { surveyId, priorityOrder, ... }
const UPDATE_ENDPOINT = (id) => `/configurations/router-survey-priority/${id}`; // PUT  { surveyId, priorityOrder, isActive, ... }

const toPriority = (v) => {
    const n = Number(v);
    if (Number.isFinite(n)) return Math.min(10, Math.max(1, Math.trunc(n)));
    return 1;
};

const formatDate = (v) => {
    if (!v) return "";
    const d = new Date(v);
    return isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};

const oneLineSx = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
};

const OneLine = ({ text, title }) => {
    const t = (text ?? "").toString();
    return (
        <Tooltip title={(title ?? t) || null}>
            <div style={oneLineSx}>{t || "-"}</div>
        </Tooltip>
    );
};

const fmtDate = (v) => {
    if (!v) return "";
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString();
};

const timeAgo = (v) => {
    if (!v) return "-";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "-";
    const sec = Math.floor((Date.now() - d.getTime()) / 1000);
    const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
    const steps = [
        ["year", 31536000], ["month", 2592000], ["week", 604800],
        ["day", 86400], ["hour", 3600], ["minute", 60], ["second", 1],
    ];
    for (const [unit, s] of steps) {
        if (sec >= s || unit === "second") return rtf.format(-Math.floor(sec / s), unit);
    }
    return "-";
};

const fmtPct = (v) => (v === null || v === undefined || v === "" ? "-" : `${Number(v).toFixed(2)}%`);
const fmtMoney = (v) => (Number.isFinite(Number(v)) ? Number(v).toFixed(2) : "-");

export default function PriorityTable({
    addOpen,
    setAddOpen,
    addSurveyId,
    setAddSurveyId,
    addPriorityOrder,
    setAddPriorityOrder,
    addIsActive,
    setAddIsActive,
    addSubmitting,
    setAddSubmitting,
    addError,
    setAddError,
    // closeAdd,
}) {
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(50);
    const [data, setData] = useState([]);

    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [rowPendingDelete, setRowPendingDelete] = useState(null);
    const [deleteSubmitting, setDeleteSubmitting] = useState(false);
    const [deleteError, setDeleteError] = useState("");

    const { currentUser } = useOutletContext();
    const CURRENT_USER_ID = currentUser?.id;
    console.log("Current User:", currentUser, CURRENT_USER_ID);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const res = await TLinkApi.get(LIST_ENDPOINT);
            // const rows = (res?.data ?? []).map((r) => ({
            //     id: r.id,
            //     survey_id: r.survey_id,
            //     survey_name: r.survey_name || "",
            //     priority_order: toPriority(r.priority_order),
            //     created_at: r.created_at || "",
            //     updated_at: r.updated_at || "",
            //     is_active: r.is_active ?? true,
            //     added_by_name: r.added_by_name ?? "",
            //     removed_by_name: r.removed_by_name ?? "",
            //     removed_at: r.removed_at ?? null,
            // }));
            const rows = (res?.data ?? []).map((r) => ({
                id: r.id,
                survey_id: r.survey_id,
                client_name: r.client_name || "",
                buyer_name: r.buyer_name || "",
                survey_status_name: r.survey_status_name || "",

                priority_order: toPriority(r.priority_order),

                created_at: r.created_at || "",           // used for “On Priority since”
                starts_at: r.starts_at || r.starts || "",
                completes_at: r.completes_at || r.complete || "",

                conversion: r.conversion ?? null,
                ecp: r.ecp ?? null,
                cpi: r.cpi ?? null,

                is_active: r.is_active ?? true,           // keep for filters/actions
            }));
            setData(rows);
        } catch (e) {
            console.log(e);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const existingSurveyIds = useMemo(
        () => new Set((data || []).map((r) => String(r.survey_id))),
        [data]
    );

    const addSurveyIdTrim = String(addSurveyId).trim();
    const isAddDuplicate = addSurveyIdTrim && existingSurveyIds.has(addSurveyIdTrim);
    const isAddInvalid =
        !addSurveyIdTrim || isAddDuplicate || !Number.isFinite(Number(addPriorityOrder));

    const columns = useMemo(() => {
        // const StatusChip = ({ value }) => {
        //     console.log("StatusChip value:", value);
        //     const v = (value || "").toString();
        //     // const palette =
        //     //     v.toLowerCase() === "active" ? { bg: "#E7F8EF", color: "#15803D" } :
        //     //         v.toLowerCase() === "paused" ? { bg: "#FFF7E0", color: "#B45309" } :
        //     //             v.toLowerCase() === "completed" ? { bg: "#E2E8F0", color: "#334155" } :
        //     //                 { bg: "#F1F5F9", color: "#475569" };
        //     return (
        //         <Tooltip title={v || null}>
        //             <div
        //                 style={{
        //                     ...oneLineSx,
        //                     display: "inline-flex",
        //                     alignItems: "center",
        //                     padding: "2px 10px",
        //                     borderRadius: 999,
        //                     // background: palette.bg,
        //                     // color: palette.color,
        //                     fontWeight: 700,
        //                     fontSize: 12,
        //                     maxWidth: "100%",
        //                 }}
        //             >
        //                 {v || "-"}
        //             </div>
        //         </Tooltip>
        //     );
        // };

        return [
            // Survey ID
            {
                accessor: "survey_id",
                Header: "Survey ID",
                align: "left",
                sortable: true,
                size: 120,
                Cell: ({ row }) => <OneLine text={row.original.survey_id} />,
            },

            // Client Name
            {
                accessor: "client_name",
                Header: "Client Name",
                align: "left",
                sortable: true,
                size: 180,
                Cell: ({ row }) => <OneLine text={row.original.client_name} />,
            },

            // Buyer Name
            {
                accessor: "buyer_name",
                Header: "Buyer Name",
                align: "left",
                sortable: true,
                size: 180,
                Cell: ({ row }) => <OneLine text={row.original.buyer_name} />,
            },

            // Status

            {
                accessor: "survey_status_name",
                Header: "Status",
                align: "left",
                sortable: true,
                size: 120,
                Cell: ({ row }) => {
                    const p = row.original.survey_status_name
                    // const styles =
                    //     p === 1 ? { bg: "#FFE5E5", color: "#E11D48" } :
                    //         p <= 4 ? { bg: "#FFF7E0", color: "#B45309" } :
                    //             { bg: "#E7F8EF", color: "#15803D" };
                    return (
                        <Tooltip title={`${p}` || null}>
                            <div
                                style={{
                                    ...oneLineSx,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    padding: "2px 10px",
                                    borderRadius: 999,
                                    // background: styles.bg,
                                    // color: styles.color,
                                    fontWeight: 700,
                                    fontSize: 12,
                                    minWidth: 36,
                                    justifyContent: "center",
                                    maxWidth: "100%",
                                }}
                            >
                                {p || "-"}
                            </div>
                        </Tooltip>
                    );
                },
            },

            // Priority Order
            {
                accessor: "priority_order",
                Header: "Priority Order",
                align: "left",
                sortable: true,
                size: 120,
                Cell: ({ row }) => {
                    const p = toPriority(row.original.priority_order);
                    // const styles =
                    //     p === 1 ? { bg: "#FFE5E5", color: "#E11D48" } :
                    //         p <= 4 ? { bg: "#FFF7E0", color: "#B45309" } :
                    //             { bg: "#E7F8EF", color: "#15803D" };
                    return (
                        <Tooltip title={`Priority ${p}` || null}>
                            <div
                                style={{
                                    ...oneLineSx,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    padding: "2px 10px",
                                    borderRadius: 999,
                                    // background: styles.bg,
                                    // color: styles.color,
                                    fontWeight: 700,
                                    fontSize: 12,
                                    minWidth: 36,
                                    justifyContent: "center",
                                    maxWidth: "100%",
                                }}
                            >
                                {p}
                            </div>
                        </Tooltip>
                    );
                },
            },

            // On Priority since (relative)
            {
                id: "on_priority_since",
                Header: "On Priority since",
                align: "left",
                sortable: true,
                size: 160,
                Cell: ({ row }) => (
                    <OneLine text={timeAgo(row.original.created_at)} title={fmtDate(row.original.created_at)} />
                ),
            },

            // Starts
            {
                accessor: "starts_at",
                Header: "Starts",
                align: "left",
                sortable: true,
                size: 50,
                Cell: ({ row }) => <OneLine text={fmtDate(row.original.starts_at)} />,
            },

            // Complete
            {
                accessor: "completes_at",
                Header: "Complete",
                align: "left",
                sortable: true,
                size: 50,
                Cell: ({ row }) => <OneLine text={fmtDate(row.original.completes_at)} />,
            },

            // Conversion
            {
                accessor: "conversion",
                Header: "Conversion",
                align: "left",
                sortable: true,
                size: 50,
                Cell: ({ row }) => <OneLine text={fmtPct(row.original.conversion)} title={String(row.original.conversion || "")} />,
            },

            // Ecp
            {
                accessor: "ecp",
                Header: "Ecp",
                align: "left",
                sortable: true,
                size: 50,
                Cell: ({ row }) => <OneLine text={fmtMoney(row.original.ecp)} />,
            },

            // Cpc
            {
                accessor: "cpi",
                Header: "Cpi",
                align: "left",
                sortable: true,
                size: 50,
                Cell: ({ row }) => <OneLine text={fmtMoney(row.original.cpi)} />,
            },

            // Action
            {
                accessor: "actions",
                Header: "Action",
                align: "center",
                headerAlign: "center",
                size: 50,
                Cell: ({ row }) => {
                    const r = row.original;
                    return (
                        <Stack direction="row" spacing={1} justifyContent="center" >
                            {r.is_active && (
                                <IconButton sx={{ p: 0 }} size="small" onClick={() => askDelete(r)}>
                                    <DeleteIcon sx={{ fontSize: 18 }} color="error" />
                                </IconButton>
                            )}
                        </Stack>
                    );
                },
            },
        ];
    }, []);


    const handleCreate = async () => {
        if (!addSurveyId || !addPriorityOrder) return;
        const surveyIdTrim = String(addSurveyId).trim();
        if (!surveyIdTrim) return;
        if (existingSurveyIds.has(surveyIdTrim)) {
            return;
        }
        setAddSubmitting(true);
        try {
            const payload = {
                surveyId: Number(addSurveyId),
                priorityOrder: Number(toPriority(addPriorityOrder)),
                isActive: Boolean(addIsActive),
                addedBy: CURRENT_USER_ID,
            };
            await TLinkApi.post(CREATE_ENDPOINT, payload);
            setAddOpen(false);
            await fetchData();
        } catch (e) {
            const msg = e?.response?.data?.message || e?.message || "Something went wrong";
            setAddError(msg);
        } finally {
            setAddSubmitting(false);
        }
    };

    const askDelete = (r) => {
        setRowPendingDelete(r);
        setDeleteError("");
        setConfirmDeleteOpen(true);
    };

    const handleSoftDelete = async () => {
        if (!rowPendingDelete?.id) return;
        setDeleteSubmitting(true);
        try {
            const payload = {
                isActive: false,
                removedBy: CURRENT_USER_ID,
                removedAt: new Date().toISOString(),
            };
            await TLinkApi.del(UPDATE_ENDPOINT(rowPendingDelete.id), payload);
            setConfirmDeleteOpen(false);
            setRowPendingDelete(null);
            await fetchData();
        } catch (e) {
            const msg =
                e?.response?.data?.message || e?.message || "Unable to remove priority. Please try again.";
            setDeleteError(msg);
        } finally {
            setDeleteSubmitting(false);
        }
    };

    const [fullScr, setFullScr] = useState(false);
    const renderToolbarInternalActions = ({ table }) => {
        return (
            <>
                <div style={{ display: "flex", alignItems: "center", gap: "0rem" }}>
                    <MRT_ShowHideColumnsButton table={table} />
                    <MRT_ToggleFiltersButton table={table} />
                    <div onClick={() => setFullScr(!fullScr)} >
                        <MRT_ToggleFullScreenButton table={table} />
                    </div>
                </div>
            </>
        );
    };

    return (
        <MDBox p={1} mt={1} mr={1} sx={{ background: "white", borderRadius: "0.5rem" }}>
            {isLoading ? (
                <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open>
                    <CircularProgress color="inherit" />
                </Backdrop>
            ) : null}

            {/* <DataTable
                table={{ columns, rows: data }}
                isSorted
                entriesPerPage={entriesPerPage}
                canSearch={false}
                tablePage={page}
                setTablePage={setPage}
                setEntriesPerPage={setEntriesPerPage}
                totalLength={data.length}
                pagination
                enableTopToolbar
                tableMaxHeight={"calc(100vh - 270px)"}
                tableMinHeight={"calc(100vh - 270px)"}
                renderTopToolbarCustomActions={() => (
                    <Stack direction="row" spacing={1} alignItems="center">
                       
                    </Stack>
                )}
                initialState={{
                    density: 'compact',
                    columnFilters: [{ id: 'is_active', value: true }],
                    // columnVisibility: { is_active: true },
                }}
            /> */}
            <MRTClientDataTable
                table={{ columns, rows: data }}
                isSorted
                canSearch={false}
                enableTopToolbar
                defaultPageSize={entriesPerPage}
                entriesOption={["10", "20", "30", "40", "50"]}
                tableMaxHeight="calc(100vh - 270px)"
                tableMinHeight="calc(100vh - 270px)"
                renderToolbarInternalActions={renderToolbarInternalActions}
                fullScr={fullScr}
                renderTopToolbarCustomActions={() => (
                    <Stack direction="row" spacing={1} alignItems="center">
                        {/* <Typography variant="h6" sx={{ fontWeight: 600, color: "black" }}>
        Priority
      </Typography> */}
                        {/* <Button variant="contained" onClick={openAdd}>
        + Priority
      </Button> */}
                    </Stack>
                )}
                initialState={{
                    density: "compact",
                    columnFilters: [{ id: "is_active", value: true }],
                    // columnVisibility: { is_active: true },
                }}
            />
            <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add Priority</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Stack spacing={2}>
                        {/* {addError && !isAddDuplicate && (
                            <Alert severity="error">{addError}</Alert>
                        )} */}
                        <FormControl fullWidth>
                            <FormLabel sx={{ mb: 0.5, fontSize: "12px" }}>Survey ID</FormLabel>
                            <TextField
                                size="small"
                                placeholder="Enter Survey ID (number)"
                                value={addSurveyId}
                                type="number"
                                onChange={(e) => {
                                    setAddSurveyId(e.target.value);
                                    setAddError("");
                                }}
                                error={Boolean(
                                    (addSurveyIdTrim && isAddDuplicate) || addError
                                )}
                                helperText={
                                    isAddDuplicate
                                        ? "This Survey ID is already in the priority list."
                                        : addError || ""
                                }
                            />
                        </FormControl>

                        <FormControl fullWidth>
                            <FormLabel sx={{ mb: 0.5, fontSize: "12px" }}>Priority Order (1–10)</FormLabel>
                            <TextField
                                type="number"
                                size="small"
                                inputProps={{ min: 1, max: 10, step: 1 }}
                                value={addPriorityOrder}
                                // onChange={(e) => setAddPriorityOrder(toPriority(e.target.value))}
                                onChange={(e) => {
                                    const raw = e.target.value.replace(/[^\d]/g, '');
                                    if (raw === '') { setAddPriorityOrder(''); return; }
                                    if (Number(raw) > 10) return;
                                    setAddPriorityOrder(raw);
                                }}
                                placeholder="Enter a number from 1 to 10"
                            />
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <MDButton variant="outlined" color="secondary" onClick={() => setAddOpen(false)}>Cancel</MDButton>
                    <MDButton variant="contained" color="info" onClick={handleCreate} disabled={addSubmitting || isAddInvalid || !addPriorityOrder || addPriorityOrder == 0}>
                        {addSubmitting ? "Saving…" : "Save"}
                    </MDButton>
                </DialogActions>
            </Dialog>

            <Dialog
                open={confirmDeleteOpen}
                onClose={() => !deleteSubmitting && setConfirmDeleteOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Remove Priority?</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Are you sure you want to remove the priority for&nbsp;
                        <strong>
                            {rowPendingDelete?.survey_name
                                ? rowPendingDelete?.survey_name
                                : `Survey #${rowPendingDelete?.survey_id}`}
                        </strong>
                        ?
                        <br />
                        This will mark the record as <b>Inactive</b>. No data is permanently deleted.
                    </Typography>
                    {deleteError ? (
                        <Typography variant="body2" color="error" mt={1}>
                            {deleteError}
                        </Typography>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <MDButton
                        variant="outlined"
                        color="secondary"
                        onClick={() => setConfirmDeleteOpen(false)}
                        disabled={deleteSubmitting}
                    >
                        Cancel
                    </MDButton>
                    <MDButton
                        variant="contained"
                        color="error"
                        onClick={handleSoftDelete}
                        disabled={deleteSubmitting}
                    >
                        {deleteSubmitting ? "Removing…" : "Confirm Remove"}
                    </MDButton>
                </DialogActions>
            </Dialog>
        </MDBox>
    );
}
